import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Users,
  ClipboardList,
  Settings,
  Phone,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAuth } from '@/hooks/useAuth';

interface NavItem {
  to:       string;
  label:    string;
  icon:     React.ReactNode;
  adminOnly?: boolean;
  requirePerm?: 'canViewAuditLogs';
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard',          icon: <LayoutDashboard className="h-5 w-5" /> },
  { to: '/content',   label: 'Content Management', icon: <FileText className="h-5 w-5" /> },
  { to: '/users',     label: 'Users',              icon: <Users className="h-5 w-5" />, adminOnly: true },
  { to: '/audit',     label: 'Audit Log',          icon: <ClipboardList className="h-5 w-5" />, requirePerm: 'canViewAuditLogs' },
  { to: '/settings',  label: 'Settings',           icon: <Settings className="h-5 w-5" />, adminOnly: true },
  { to: '/rosary',    label: 'Chain Rosary',       icon: <Phone className="h-5 w-5" />,   adminOnly: true },
];

export default function Sidebar() {
  const { isAdmin, canViewAuditLogs } = useAuth();

  const isVisible = (item: NavItem) => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.requirePerm === 'canViewAuditLogs' && !canViewAuditLogs && !isAdmin) return false;
    return true;
  };

  return (
    <aside className="fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-surface-900 text-white">

      {/* WombCat Logo */}
      <div className="flex flex-col items-center gap-2 border-b border-surface-700 px-5 py-4">
        <img
          src="/wombcat-logo.png"
          alt="WombCat"
          className="w-16 h-16 object-contain"
        />
        <div className="text-center">
          <p className="text-sm font-bold leading-none text-white">WombCat</p>
          <p className="text-xs text-surface-400 leading-none mt-0.5">CMS</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {NAV_ITEMS.filter(isVisible).map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/dashboard'} // only exact-match dashboard; content/* stays active for sub-routes
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary-600 text-white'
                  : 'text-surface-300 hover:bg-surface-800 hover:text-white'
              )
            }
          >
            {item.icon}
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* Angels Army Ministry Logo */}
      <div className="border-t border-surface-700 px-5 py-4 flex flex-col items-center gap-2">
        <img
          src="/angels-army-logo.png"
          alt="Angels Army Ministry"
          className="w-10 h-10 object-contain opacity-80"
        />
        <p className="text-[10px] text-surface-500 text-center leading-tight">
          Angels Army Ministry
        </p>
      </div>
    </aside>
  );
}
